/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Stack,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import RegistrationBenefits from '../../pages/account/registration-benefits/registration-benefits'
import {useGigya} from '../../context/gigya'
import GigyaRegisterController from '../../utils/gigya/registration'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {hashSHA256} from '../../utils/utils'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useWishList} from '../../hooks/use-wish-list'
import {BRAND_KEY} from '../../brand'
import GigyaBasicFormController from '../../utils/gigya/lite-registration'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useGigyaSSR from '../../custom-hooks/use-gigya-ssr'
import {Global} from '@emotion/react'

const RegisterForm = ({
    isThankYouPage = false,
    email,
    variant,
    isProductRegistrationPage = false,
}) => {
    const {gigyaObject, gigyaLocale} = useGigya()
    const stylesLogin = useMultiStyleConfig('Login')
    const stylesGlobal = useMultiStyleConfig('GigyaGlobal')
    const {addLayer} = useGoogleTagManager()
    const navigate = useNavigation()
    const intl = useIntl()
    const ssrGigyaMethods = useGigyaSSR()
    const {getAccountInfo, getLiteToken} = ssrGigyaMethods

    const {data: basket} = useCurrentBasket()
    const {data: wishListData} = useWishList()

    useEffect(() => {
        if (!gigyaObject) return

        window.DLG = {
            ...(window.DLG || {}),
            EVE: {
                emit: (e) => {
                    if (e === 'GIGYA.REDIRECT.LOGIN') {
                        gigyaObject?.accounts?.hideScreenSet({
                            screenSet: 'Default-RegistrationLogin',
                        })
                        navigate('/login')
                    }
                },
            },
            gigya: {brand: BRAND_KEY},
        }
        return () => {
            delete window.DLG
        }
    }, [gigyaObject])

    useEffect(() => {
        if (!gigyaObject) return

        gigyaObject.accounts.showScreenSet({
            screenSet: 'Default-RegistrationLogin',
            startScreen: 'gigya-register-screen',
            containerID: 'register',
            lang: gigyaLocale,
            deviceType: 'desktop',
            onSubmit: (e) => {
                GigyaRegisterController.onSubmit(
                    e,
                    null,
                    isThankYouPage && !isProductRegistrationPage
                        ? 'ECOMMERCE_GUEST'
                        : isThankYouPage && isProductRegistrationPage
                        ? 'PRODUCT_REGISTRATION_GUEST'
                        : null,
                )
            },
            onBeforeScreenLoad: (event) => {
                if (!event.context || event.context != 'ownid-reg-completion') {
                    //For registration completion screen
                    if (
                        event.nextScreen === 'gigya-complete-registration-screen' &&
                        String(event?.response?.errorCode) === '206001'
                    ) {
                        GigyaRegisterController.showCompleteRegistration({
                            gigyaObject,
                            event,
                            gigyaLocale,
                            GigyaRegisterController,
                            GigyaBasicFormController,
                        })

                        return false
                    }

                    if (!event.context) {
                        if (
                            event.nextScreen === 'gigya-verification-pending-screen' &&
                            String(event?.response?.errorCode) === '206002'
                        ) {
                            gigyaObject.accounts.showScreenSet({
                                screenSet: 'Default-RegistrationLogin',
                                startScreen: 'gigya-verification-pending-screen',
                                regToken:
                                    event?.response?.response?.regToken ||
                                    event?.response?.regToken,
                                lang: gigyaLocale,
                                onBeforeScreenLoad: () => {
                                    const closeBtn = document.querySelector(
                                        '.gigya-screen-dialog-top .gigya-screen-dialog-close a',
                                    )

                                    const oldImg = closeBtn?.querySelector('img')
                                    if (oldImg) {
                                        oldImg.remove()

                                        const svg = document.createElement('span')
                                        svg.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6 6L18 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
                                        closeBtn.appendChild(svg)
                                    }

                                    const modalTitle = document.querySelector(
                                        '.gigya-screen-dialog-top .gigya-screen-dialog-caption',
                                    )

                                    if (modalTitle) {
                                        modalTitle.style.display = 'none'

                                        const customTitle = document.createElement('div')
                                        customTitle.className = modalTitle.className
                                        customTitle.id = 'custom-modal-title'

                                        customTitle.textContent = intl.formatMessage({
                                            id: 'screensets.registration_pending.title',
                                            defaultMessage: 'Your account is almost ready',
                                        })

                                        modalTitle.parentNode.insertBefore(customTitle, modalTitle)
                                    }
                                },
                            })
                            return false
                        }
                    }
                }
            },
            onAfterScreenLoad: async (e) => {
                if (isProductRegistrationPage) {
                    const wrapper = document.getElementById(e.sourceContainerID)
                    if (!wrapper) return

                    const fields = [
                        {name: 'profile.firstName', value: basket?.c_firstName},
                        {name: 'profile.lastName', value: basket?.c_lastName},
                    ]

                    fields.forEach(({name, value}) => {
                        const input = wrapper.querySelector(`input[name="${name}"]`)
                        if (!input) return

                        const labelWrapper = wrapper.querySelector(`label[for="${input.id}"]`)
                        if (labelWrapper) {
                            labelWrapper.style.display = 'none'
                        }

                        const labelTextEl = labelWrapper?.querySelector('.gigya-label-text')
                        const requiredEl = labelWrapper?.querySelector('.gigya-required-display')
                        const labelText = labelTextEl?.textContent?.trim() || ''
                        input.value = value || ''
                        input.placeholder = labelText + (requiredEl ? '*' : '')
                    })
                }
                GigyaRegisterController.onAfterScreenLoad(e, isThankYouPage, email)

                const buildCustomSocialButtons = () => {
                    const uiContainer = document.querySelector('#register [id^="register_social_"]')
                    if (!uiContainer || document.getElementById('register-social-custom')) return

                    const providers = [...uiContainer.querySelectorAll('.gigya-login-provider')]
                    if (!providers.length) return

                    const providerMeta = {
                        facebook: {
                            label: 'Facebook',
                            icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" fill="#1877F2"/></svg>`,
                        },
                        googleplus: {
                            label: 'Google',
                            icon: `<svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>`,
                        },
                    }

                    const row = document.createElement('div')
                    row.id = 'register-social-custom'

                    providers.forEach((provSpan) => {
                        const providerName = provSpan.getAttribute('data-gigya-provider') || ''
                        const meta = providerMeta[providerName] || {label: providerName, icon: ''}
                        const btn = document.createElement('button')
                        btn.type = 'button'
                        btn.className = 'login-social-btn'
                        btn.innerHTML = `<span style="display:flex;align-items:center;line-height:0">${meta.icon}</span><span>${meta.label}</span>`
                        btn.addEventListener('click', () => {
                            const trigger = provSpan.querySelector('button') || provSpan
                            trigger.click()
                        })
                        row.appendChild(btn)
                    })

                    uiContainer.style.setProperty('display', 'none', 'important')
                    uiContainer.parentElement.appendChild(row)
                }
                buildCustomSocialButtons()
                setTimeout(buildCustomSocialButtons, 300)

                const response = await getLiteToken(email)
                const data = await getAccountInfo(null, response?.token)
                if (isThankYouPage) {
                    // Prefill marketing consent checkbox
                    const lang = window.gigya.thisScript.lang.originalLang
                    const countryCode = window.gigya.thisScript.globalConf.countryCodeMapping[lang]
                    const hasConsent = Object.values(data?.preferences || {})?.some(
                        (pref) =>
                            pref?.entitlements.includes(`${BRAND_KEY}${countryCode}`) &&
                            pref?.isConsentGranted === true,
                    )
                    const checkboxContainer = document.querySelector(
                        '#register form div.gigya-composite-control.gigya-composite-control-checkbox.gigya-custom-checkbox',
                    )

                    if (isProductRegistrationPage && !hasConsent) {
                        checkboxContainer.style.setProperty('display', 'flex')
                    }
                    if (hasConsent) {
                        const checkbox = document.querySelector(
                            '#register form div.gigya-layout-row div.gigya-custom-checkbox input.gigya-input-checkbox',
                        )
                        setTimeout(() => {
                            checkbox.click()
                        }, 100)
                    }
                }
            },
            onBeforeValidation: () => {},
            onError: () => {},
            onFieldChanged: () => {},
            onBeforeSubmit: (res) => GigyaRegisterController.onBeforeSubmit(res),
            onAfterSubmit: async (res) => {
                const brands = res?.response?.data?.brands || {}
                GigyaRegisterController.onAfterSubmit(res, ssrGigyaMethods, brands)
                await addLayer(
                    basic_gtm_event.bind(this, {
                        event: 'reservedArea',
                        eventCategory: 'loyalty',
                        eventAction: 'reservedArea',
                        eventLabel: 'predoubleoptin-signUp',
                        userId: res && res.response && res.response.UID,
                        email: res && res.profile && res.profile.email,
                        emailSHA256: await hashSHA256(res.profile.email),
                    }),
                )
                localStorage.setItem('guestBasket', JSON.stringify(basket))
                localStorage.setItem('guestWishList', JSON.stringify(wishListData))
            },
        })
    }, [gigyaObject])

    return (
        <>
            <Global styles={stylesGlobal['.chakra-ui-light']} />
            <Stack
                {...(isThankYouPage
                    ? stylesLogin.registrationFormStackThankYou
                    : variant === 'preCheckout'
                    ? stylesLogin.registrationFormStackSubscriptions
                    : stylesLogin.registrationFormStack)}
                __css={stylesLogin}
            >
                {isThankYouPage && !isProductRegistrationPage && (
                    <Text {...stylesLogin.thankYouRegistrationTitle}>
                        <FormattedMessage
                            defaultMessage="Email"
                            id="login_form.message.registration_title_thank_you_guest"
                        />
                    </Text>
                )}
                {isProductRegistrationPage && (
                    <Text {...stylesLogin.thankYouRegistrationTitle}>
                        {' '}
                        <FormattedMessage
                            defaultMessage="New user?"
                            id="login_form.message.new_user"
                        />
                    </Text>
                )}
                <div className="login-content" id="register"></div>
            </Stack>
            {variant !== 'preCheckout' && (
                <Stack {...stylesLogin.registrationStack}>
                    {isThankYouPage ? (
                        <Text {...stylesLogin.thankYouRegistrationTitleCreate}>
                            <FormattedMessage
                                defaultMessage="Create an account and register your product"
                                id="login_form.message.benefits_title_thank_you_guest"
                            />
                        </Text>
                    ) : (
                        <>
                            <Text {...stylesLogin.registrationTitle}>
                                <FormattedMessage
                                    defaultMessage="Your benefits"
                                    id="login_form.message.registration_title"
                                />
                            </Text>
                            <Text {...stylesLogin.registrationSubtitle}>
                                <FormattedMessage
                                    defaultMessage="Join the De’Longhi family to take advantage of exclusive deals and a more customised experience."
                                    id="login_form.message.registration_subtitle"
                                />
                            </Text>
                        </>
                    )}

                    <RegistrationBenefits isThankYouPage={isThankYouPage} />
                </Stack>
            )}
        </>
    )
}

RegisterForm.propTypes = {
    isThankYouPage: PropTypes.bool,
    email: PropTypes.string,
    variant: PropTypes.string,
    isProductRegistrationPage: PropTypes.bool,
}

export default RegisterForm
